import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Stack, TextField, MenuItem, Select, FormControl, InputLabel, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Pagination, LinearProgress, Alert, Chip, Tooltip, Collapse, IconButton } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { worldcheckService, WorldCheckEntityListItem, UploadResponse } from '../services/worldcheckService';

const DEFAULT_PAGE_SIZE = 25;

const WorldCheckPage: React.FC = () => {
	const [file, setFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [entities, setEntities] = useState<WorldCheckEntityListItem[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [country, setCountry] = useState('');
	const [entityType, setEntityType] = useState('');
	const [riskLevel, setRiskLevel] = useState('');
	const [pep, setPep] = useState('');
	const [sanctioned, setSanctioned] = useState('');
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
	const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);

	const toggleRowExpansion = (entityId: string) => {
		const newExpanded = new Set(expandedRows);
		if (newExpanded.has(entityId)) {
			newExpanded.delete(entityId);
		} else {
			newExpanded.add(entityId);
		}
		setExpandedRows(newExpanded);
	};

	const getRiskColor = (risk: string | null | undefined): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
		switch (risk?.toLowerCase()) {
			case 'critical': return 'error';
			case 'high': return 'warning';
			case 'medium': return 'info';
			case 'low': return 'success';
			default: return 'default';
		}
	};

	const fetchEntities = async () => {
		try {
			setLoading(true);
			setError(null);
			const resp = await worldcheckService.list({
				skip: (page - 1) * DEFAULT_PAGE_SIZE,
				limit: DEFAULT_PAGE_SIZE,
				search: search || undefined,
				country: country || undefined,
				entity_type: entityType || undefined,
				risk_level: riskLevel || undefined,
				pep: pep === '' ? undefined : pep === 'true',
				sanctioned: sanctioned === '' ? undefined : sanctioned === 'true',
			});
			setEntities(resp.entities);
			setTotal(resp.total);
		} catch (e: any) {
			setError(e?.response?.data?.detail || 'Failed to load entities');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchEntities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	const handleUpload = async () => {
		if (!file) return;
		try {
			setUploading(true);
			setError(null);
			setUploadResult(null);
			const result = await worldcheckService.upload(file);
			setUploadResult(result);
			setFile(null);
			setPage(1);
			await fetchEntities();
			// Show success message
			setError(null);
		} catch (e: any) {
			if (e.code === 'ECONNABORTED') {
				setError('Upload timed out. The file may be too large. Please try a smaller file or contact support.');
			} else {
				setError(e?.response?.data?.detail || 'Upload failed');
			}
		} finally {
			setUploading(false);
		}
	};

	return (
		<Box>
			<Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>WorldCheck</Typography>
			<Paper sx={{ p: 2, mb: 3 }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
					<Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={uploading}>
						{file ? file.name : 'Select CSV/TSV'}
						<input type="file" hidden accept=".csv,.tsv,.tab,text/csv,text/tab-separated-values" onChange={(e) => setFile(e.target.files?.[0] || null)} />
					</Button>
					<Button variant="contained" onClick={handleUpload} disabled={!file || uploading}>
						{uploading ? 'Uploading...' : 'Upload'}
					</Button>
					{uploading && <LinearProgress sx={{ minWidth: 160 }} />}
				</Stack>
			</Paper>

			{uploadResult && (
				<Paper sx={{ p: 2, mb: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
					<Typography variant="h6" gutterBottom>Upload Results</Typography>
					<Stack direction="row" spacing={4} flexWrap="wrap">
						<Box>
							<Typography variant="body2" color="inherit">
								<strong>{uploadResult.inserted}</strong> entities inserted
							</Typography>
						</Box>
						{uploadResult.duplicates_skipped > 0 && (
							<Box>
								<Typography variant="body2" color="inherit">
									<strong>{uploadResult.duplicates_skipped}</strong> duplicates skipped
								</Typography>
							</Box>
						)}
						{uploadResult.errors > 0 && (
							<Box>
								<Typography variant="body2" color="inherit">
									<strong>{uploadResult.errors}</strong> errors encountered
								</Typography>
							</Box>
						)}
						<Box>
							<Typography variant="body2" color="inherit">
								<strong>{uploadResult.total_processed}</strong> total processed
							</Typography>
						</Box>
					</Stack>
				</Paper>
			)}

			<Paper sx={{ p: 2, mb: 2 }}>
				<Typography variant="h6" gutterBottom>Filters</Typography>
				<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
					<TextField 
						label="Search name" 
						value={search} 
						onChange={(e) => setSearch(e.target.value)} 
						onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchEntities(); } }}
						sx={{ minWidth: 200 }}
					/>
					<TextField 
						label="Country" 
						value={country} 
						onChange={(e) => setCountry(e.target.value)}
						sx={{ minWidth: 150 }}
					/>
					<TextField 
						label="Entity Type" 
						value={entityType} 
						onChange={(e) => setEntityType(e.target.value)} 
						placeholder="person | organization"
						sx={{ minWidth: 150 }}
					/>
					<FormControl sx={{ minWidth: 140 }}>
						<InputLabel id="risk-select">Risk Level</InputLabel>
						<Select labelId="risk-select" label="Risk Level" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
							<MenuItem value="">Any</MenuItem>
							<MenuItem value="low">Low</MenuItem>
							<MenuItem value="medium">Medium</MenuItem>
							<MenuItem value="high">High</MenuItem>
							<MenuItem value="critical">Critical</MenuItem>
						</Select>
					</FormControl>
					<FormControl sx={{ minWidth: 140 }}>
						<InputLabel id="pep-select">PEP</InputLabel>
						<Select labelId="pep-select" label="PEP" value={pep} onChange={(e) => setPep(e.target.value)}>
							<MenuItem value="">Any</MenuItem>
							<MenuItem value="true">Yes</MenuItem>
							<MenuItem value="false">No</MenuItem>
						</Select>
					</FormControl>
					<FormControl sx={{ minWidth: 160 }}>
						<InputLabel id="sanctioned-select">Sanctioned</InputLabel>
						<Select labelId="sanctioned-select" label="Sanctioned" value={sanctioned} onChange={(e) => setSanctioned(e.target.value)}>
							<MenuItem value="">Any</MenuItem>
							<MenuItem value="true">Yes</MenuItem>
							<MenuItem value="false">No</MenuItem>
						</Select>
					</FormControl>
				</Stack>
				<Stack direction="row" spacing={2}>
					<Button variant="contained" onClick={() => { setPage(1); fetchEntities(); }}>
						Apply Filters
					</Button>
					<Button variant="outlined" onClick={() => {
						setSearch('');
						setCountry('');
						setEntityType('');
						setRiskLevel('');
						setPep('');
						setSanctioned('');
						setPage(1);
						fetchEntities();
					}}>
						Clear All
					</Button>
				</Stack>
			</Paper>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

			<Paper>
				{loading && <LinearProgress />}
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell width="40px"></TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Country</TableCell>
								<TableCell>DOB</TableCell>
								<TableCell>Place of Birth</TableCell>
								<TableCell>Risk</TableCell>
								<TableCell>PEP</TableCell>
								<TableCell>Sanctioned</TableCell>
								<TableCell>Categories</TableCell>
								<TableCell>Last Seen</TableCell>
								<TableCell>Updated</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{entities.map((e) => (
								<React.Fragment key={e.id}>
									<TableRow hover>
										<TableCell>
											<IconButton size="small" onClick={() => toggleRowExpansion(e.id)}>
												{expandedRows.has(e.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
											</IconButton>
										</TableCell>
										<TableCell>
											<Tooltip title={e.full_name} placement="top">
												<Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
													{e.full_name}
												</Box>
											</Tooltip>
										</TableCell>
										<TableCell>{e.entity_type || '-'}</TableCell>
										<TableCell>{e.country || '-'}</TableCell>
										<TableCell>{e.date_of_birth ? new Date(e.date_of_birth).toLocaleDateString() : '-'}</TableCell>
										<TableCell>
											<Tooltip title={e.place_of_birth || ''} placement="top">
												<Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
													{e.place_of_birth || '-'}
												</Box>
											</Tooltip>
										</TableCell>
										<TableCell>
											{e.risk_level ? (
												<Chip label={e.risk_level} color={getRiskColor(e.risk_level)} size="small" />
											) : '-'}
										</TableCell>
										<TableCell>
											{e.pep === true ? <Chip label="Yes" color="warning" size="small" /> : 
											 e.pep === false ? <Chip label="No" color="default" size="small" /> : '-'}
										</TableCell>
										<TableCell>
											{e.sanctioned === true ? <Chip label="Yes" color="error" size="small" /> : 
											 e.sanctioned === false ? <Chip label="No" color="default" size="small" /> : '-'}
										</TableCell>
										<TableCell>
											{e.categories && Array.isArray(e.categories) && e.categories.length > 0 ? (
												<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
													{e.categories.slice(0, 2).map((cat, idx) => (
														<Chip key={idx} label={cat} size="small" variant="outlined" />
													))}
													{e.categories.length > 2 && (
														<Chip label={`+${e.categories.length - 2}`} size="small" variant="outlined" />
													)}
												</Box>
											) : '-'}
										</TableCell>
										<TableCell>{e.last_seen ? new Date(e.last_seen).toLocaleDateString() : '-'}</TableCell>
										<TableCell>{e.updated_at ? new Date(e.updated_at).toLocaleString() : '-'}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={12} sx={{ py: 0 }}>
											<Collapse in={expandedRows.has(e.id)} timeout="auto" unmountOnExit>
												<Box sx={{ p: 2, bgcolor: 'background.default' }}>
													<Stack spacing={2}>
														<Box>
															<Typography variant="subtitle2" gutterBottom>Additional Details</Typography>
															<Stack direction="row" spacing={4} flexWrap="wrap">
																<Box>
																	<Typography variant="caption" color="text.secondary">Created:</Typography>
																	<Typography variant="body2">{e.created_at ? new Date(e.created_at).toLocaleString() : '-'}</Typography>
																</Box>
																<Box>
																	<Typography variant="caption" color="text.secondary">Entity ID:</Typography>
																	<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{e.id}</Typography>
																</Box>
															</Stack>
														</Box>
														
														{e.aliases && Array.isArray(e.aliases) && e.aliases.length > 0 && (
															<Box>
																<Typography variant="subtitle2" gutterBottom>Aliases</Typography>
																<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
																	{e.aliases.map((alias, idx) => (
																		<Chip key={idx} label={alias} size="small" variant="outlined" />
																	))}
																</Box>
															</Box>
														)}

														{e.identifiers && Array.isArray(e.identifiers) && e.identifiers.length > 0 && (
															<Box>
																<Typography variant="subtitle2" gutterBottom>Identifiers</Typography>
																<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
																	{e.identifiers.map((id, idx) => (
																		<Chip key={idx} label={typeof id === 'object' ? JSON.stringify(id) : id} size="small" variant="outlined" />
																	))}
																</Box>
															</Box>
														)}

														{e.sources && Array.isArray(e.sources) && e.sources.length > 0 && (
															<Box>
																<Typography variant="subtitle2" gutterBottom>Sources</Typography>
																<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
																	{e.sources.map((source, idx) => (
																		<Chip key={idx} label={typeof source === 'object' ? JSON.stringify(source) : source} size="small" variant="outlined" />
																	))}
																</Box>
															</Box>
														)}

														{e.listing_details && (
															<Box>
																<Typography variant="subtitle2" gutterBottom>Listing Details</Typography>
																<Typography variant="body2" sx={{ 
																	whiteSpace: 'pre-wrap', 
																	maxHeight: '200px', 
																	overflow: 'auto',
																	bgcolor: 'background.default',
																	p: 1,
																	borderRadius: 1,
																	fontSize: '0.85rem'
																}}>{e.listing_details}</Typography>
															</Box>
														)}
													</Stack>
												</Box>
											</Collapse>
										</TableCell>
									</TableRow>
								</React.Fragment>
							))}
							{!loading && entities.length === 0 && (
								<TableRow>
									<TableCell colSpan={12} align="center">No entities found</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
					<Pagination
						count={Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE))}
						page={page}
						onChange={(_, p) => setPage(p)}
						color="primary"
					/>
				</Box>
			</Paper>
		</Box>
	);
};

export default WorldCheckPage;


